import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { FormikProps } from 'formik';

interface FloatingFooterProps<T> {
  formik: FormikProps<T>;
  onPrev: () => void;
  isFirstStep: boolean;
  nextLabel: string;
  renderCenter?: () => React.ReactNode;
  onHeightChange?: (px: number) => void;
  leftOffset?: number;
  borderRadius?: number;
}

export const FloatingFooter = <T,>({
  formik,
  onPrev,
  isFirstStep,
  nextLabel,
  renderCenter,
  onHeightChange,
  leftOffset = 124,
  borderRadius = 12
}: FloatingFooterProps<T>) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !onHeightChange) return;
    const observer = new ResizeObserver(([entry]) => {
      onHeightChange(entry.contentRect.height + 14);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <div
      ref={containerRef}
      className="px-4 py-2"
      style={{
        position: 'fixed',
        bottom: 24,
        left: leftOffset,
        right: 24,
        zIndex: 100,
        borderRadius,
        border: '0.5px solid #E5E7EB',
        backgroundColor: 'white',
        boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.10)'
      }}
      data-test="onboarding-footer"
    >
      <div className="d-flex align-items-center">
        <Button
          variant="link"
          disabled={isFirstStep}
          onClick={onPrev}
          data-test="prev-button"
          className="flex-shrink-0"
        >
          {intl.formatMessage({ id: 'prev' })}
        </Button>

        {renderCenter && (
          <div
            className="flex-grow-1 d-flex justify-content-center flex-wrap gap-2 mx-3"
            style={{ minWidth: 0 }}
          >
            {renderCenter()}
          </div>
        )}

        <Button
          variant="primary"
          type="submit"
          disabled={!formik.isValid}
          className="ms-auto flex-shrink-0"
          data-test="next-button"
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
};
